import { useAtom } from "jotai";
import type React from "react";
import { useTranslation } from "react-i18next";
import { CustomAvatar } from "@/components/common/CustomAvatar";
import { ListContainer, ListItem } from "@/components/common/ListItem";
import { Button } from "@/components/ui/button";
import { useMyStudyGroupsPaginated } from "@/hooks/useMyStudyGroupsPaginated";
import { useStudyGroupNavigation } from "@/hooks/useStudyGroupNavigation";
import { hasStudyGroupIdInPathAtom, studyGroupAtom } from "@/stores/studyGroupAtoms";

interface StudyGroupListProps {
  containerHeight?: string | number;
}

export const StudyGroupList: React.FC<StudyGroupListProps> = ({ containerHeight = "450px" }) => {
  const { navigateToStudyGroup } = useStudyGroupNavigation();
  const [studyGroup] = useAtom(studyGroupAtom);
  const [hasStudyGroupIdInPath] = useAtom(hasStudyGroupIdInPathAtom);
  const { t } = useTranslation();

  const { accumulatedGroups, isLoading, isLoadingMore, error, hasMore, loadMore } =
    useMyStudyGroupsPaginated("newer", 50);

  const handleGroupClick = (
    containerId: number,
    studyGroupId: number,
    guid: string,
    name: string,
    imageUrl: string,
    joinPolicy: number,
  ) => {
    navigateToStudyGroup(containerId, studyGroupId, guid, name, imageUrl, joinPolicy);
  };

  const renderContent = () => {
    if (isLoading) {
      return <div className="py-4 text-center text-[#565A5B]">{t("common.loading")}</div>;
    }

    if (error) {
      return <div className="py-4 text-center text-red-500">{error}</div>;
    }

    if (!accumulatedGroups || accumulatedGroups.length === 0) {
      return (
        <div className="py-4 text-center text-[#565A5B]">{t("common.noStudyGroupsFound")}</div>
      );
    }

    return (
      <>
        {accumulatedGroups.map((group) => (
          <ListItem
            key={group.id}
            icon={
              <div className="relative">
                <CustomAvatar name={group.name} imageUrl={group.image_url} size={32} />
              </div>
            }
            label={group.name}
            onClick={() =>
              handleGroupClick(
                Number(group.contentcontainer_id),
                group.id,
                group.guid,
                group.name,
                group.image_url,
                group.join_policy,
              )
            }
            selected={
              hasStudyGroupIdInPath && studyGroup.containerId === Number(group.contentcontainer_id)
            }
          />
        ))}

        {hasMore && (
          <div className="p-2 text-center">
            <Button variant="teal" onClick={loadMore} disabled={isLoadingMore}>
              {t("common.showMore")}
            </Button>
          </div>
        )}
      </>
    );
  };

  return (
    <ListContainer
      headerProps={{
        title: t("common.studyGroups"),
      }}
      containerHeight={containerHeight}
      className="overflow-x-hidden"
    >
      {renderContent()}
    </ListContainer>
  );
};
